import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageCircle } from "lucide-react";

export default function ChaseDialog({ alert, onClose, onSent }) {
  const [p, setP] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!alert) return;
    setP(null); setMsg("");
    api.get("/late-fees/chase-preview", { params: {
      client_id: alert.client_id, form: alert.form, due_date: alert.due_date, period: alert.period,
      days_overdue: alert.days_overdue, estimated_fee: alert.estimated_fee, fee_note: alert.fee_note,
    } }).then(r => { setP(r.data); setMsg(r.data.message); })
      .catch(e => toast.error("Could not prepare message", { description: e.response?.data?.detail || e.message }));
  }, [alert]);

  const send = async (channel) => {
    setBusy(channel);
    try {
      const { data } = await api.post("/late-fees/chase", {
        client_id: alert.client_id, form: alert.form, period: alert.period, due_date: alert.due_date,
        days_overdue: alert.days_overdue, estimated_fee: alert.estimated_fee, fee_note: alert.fee_note, channel, message: msg,
      });
      if (data.link) {
        window.open(data.link, "_blank", "noopener");
        toast.success("WhatsApp opened", { description: `Message pre-filled for ${data.to}` });
      } else {
        toast.success(channel === "email" ? "Email sent" : "WhatsApp sent", { description: `${alert.client_name} · ${data.to}` });
      }
      onSent?.(); onClose();
    } catch (e) { toast.error("Send failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={!!alert} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="chase-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl">Chase {alert?.client_name}</DialogTitle>
          <DialogDescription>{alert?.form} · {alert?.period} · {alert?.days_overdue}d overdue · est. ₹{Number(alert?.estimated_fee || 0).toLocaleString("en-IN")}</DialogDescription>
        </DialogHeader>
        {!p ? <div className="text-sm text-zinc-500 py-6">Preparing message…</div> : (
          <>
            <div className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500">Message (editable)</div>
            <Textarea data-testid="chase-message" rows={10} value={msg} onChange={(e) => setMsg(e.target.value)} className="text-sm rounded-none" />
            <div className="text-[11px] text-zinc-600 flex flex-wrap gap-x-4">
              <span>Email: <b>{p.email || "— not on file"}</b></span>
              <span>WhatsApp: <b>{p.phone || "— not on file"}</b> {p.phone ? <span className="text-zinc-400">({p.whatsapp_via === "twilio" ? "sends via Twilio" : "opens WhatsApp with text pre-filled"})</span> : null}</span>
            </div>
          </>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button data-testid="chase-send-whatsapp" disabled={!p || !p.phone || !!busy} onClick={() => send("whatsapp")} variant="outline" className="rounded-none border-zinc-300">
            <MessageCircle size={14} className="mr-1.5" /> {busy === "whatsapp" ? "Sending…" : "WhatsApp"}
          </Button>
          <Button data-testid="chase-send-email" disabled={!p || !p.email || !!busy} onClick={() => send("email")} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">
            <Mail size={14} className="mr-1.5" /> {busy === "email" ? "Sending…" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
